import type { SharePointConnectionChoice } from '@/features/sharepoint/connection-choice.js';
import type {
  SharePointPlace,
  SharePointPlaceLevel,
  SharePointPlacePolicy,
} from '@/lib/api/sharepoint.js';
import type { KnowledgeSource } from '@/lib/api/workspace-knowledge.js';

/**
 * Whether a SharePoint knowledge source's own registered address sits inside an allowed connection's
 * place set — read here rather than stored, because it is computed on read from data both sides
 * already hold (spec §10): the interface has the source's address and the connection's places, and a
 * stored flag would be a third state to keep in agreement with the two sources of truth it describes.
 *
 * `'unknown'` is a real answer, not a placeholder for `'inside'`: it means this comparison genuinely
 * cannot be made from what is available — which connection a source belongs to, or that connection's
 * places, has not resolved yet. A caller renders nothing for it rather than guessing, because the mark
 * exists to flag a DEFINITE problem, and an uncertain one is not that.
 */
export type SharePointSourceCoverage = 'inside' | 'outside' | 'unknown';

/** The source kinds that live at a SharePoint address, and so can fall outside the list. */
const SHAREPOINT_SOURCE_TYPES = new Set([
  'sharepoint_site',
  'sharepoint_library',
  'sharepoint_folder',
  'sharepoint_file',
]);

/**
 * Which connection a SharePoint source's access lives on: its own `config.connection_id` when it
 * names one, or the workspace's single shared connection when it does not — a source registered
 * before connections had ids, or one whose caller left the choice to the service's own default, both
 * resolve to the shared connection the same way the indexer itself falls back to it.
 *
 * `null` when this cannot be answered honestly: zero or more than one connection carries
 * `scope: 'workspace'` here. Two shared connections is not a configuration this platform actually
 * produces, but a source naming neither is a real, common case this has to cover without picking one
 * of several candidates at random.
 */
export function sharePointSourceConnectionId(
  source: Pick<KnowledgeSource, 'config'>,
  connections: readonly SharePointConnectionChoice[],
): string | null {
  const named = source.config.connection_id;
  if (named) return named;
  const shared = connections.filter(
    (connection) => connection.scope === 'workspace',
  );
  return shared.length === 1 ? shared[0]!.connectionId : null;
}

/**
 * Whether one address sits inside at least one of a connection's allowed places, at whichever level
 * a place is. A site place is matched by its site, every other level by its drive, the way the
 * server's guard matches them: a drive id is unique, while a site id can reach the two sides
 * spelled differently.
 *
 * Folder containment is checked by EXACT match only, never by ancestry: a knowledge source's stored
 * address carries only its own site/drive/item id, with no ancestor chain, so proving a source nested
 * a level or more beneath an allowed folder is genuinely inside it would need a live Graph ancestor
 * walk this comparison has no way to make (unlike the server's own enforcement, which does walk the
 * chain — see spec §3). Reading such a source as "outside" is the safe direction for an informational
 * mark: it costs the reader an extra glance at a source that in fact still works, never a false "this
 * is fine" about one the agent cannot reach.
 */
function isAddressInsideAnyPlace(
  siteId: string,
  driveId: string | undefined,
  itemId: string | undefined,
  places: readonly SharePointPlace[],
): boolean {
  return places.some((place) => {
    if (place.level === 'site') return place.siteId === siteId;
    if (!driveId || place.driveId !== driveId) return false;
    if (place.level === 'library') return true;
    return place.rootItemId === itemId;
  });
}

/**
 * Whether a knowledge source falls outside its connection's allowed places, for the mark on the
 * knowledge-sources screen (spec §10) and the list the restrict dialog shows before it enforces
 * anything (spec §13.1).
 *
 * Only a SharePoint source — a whole site, a library, a folder or a file — is ever `'outside'`;
 * every other kind reads `'inside'`, since the concept does not apply to it. A site or a library is
 * addressed by its own id, so the same rule answers it exactly: a site is covered only by a place on
 * that site, a library by that site or by the library itself. An unenforced connection also reads
 * `'inside'` for every one of its sources: the whole point of the flag is what enforcement would
 * remove, and nothing is removed while it is off.
 */
export function sharePointSourceCoverage(
  source: KnowledgeSource,
  connections: readonly SharePointConnectionChoice[],
  policyByConnection: ReadonlyMap<string, SharePointPlacePolicy | undefined>,
): SharePointSourceCoverage {
  if (!SHAREPOINT_SOURCE_TYPES.has(source.type)) return 'inside';
  const connectionId = sharePointSourceConnectionId(source, connections);
  if (!connectionId) return 'unknown';
  const policy = policyByConnection.get(connectionId);
  if (!policy) return 'unknown';
  if (!policy.enforced) return 'inside';
  const siteId = source.config.site_id;
  if (!siteId) return 'unknown';
  return isAddressInsideAnyPlace(
    siteId,
    source.config.drive_id,
    source.config.item_id,
    policy.places,
  )
    ? 'inside'
    : 'outside';
}

/**
 * Whether a source's own address would contain a place already on the list.
 *
 * Identity refuses a place that overlaps another, so such a source cannot become a place in one
 * press — the press could only fail. The connection's list offers Replace and add for exactly this.
 * A file contains nothing; a folder contains what has it among its ancestors.
 */
export function sourceContainsPlace(
  source: Pick<KnowledgeSource, 'type' | 'config'>,
  places: readonly SharePointPlace[],
): boolean {
  const { site_id: siteId, drive_id: driveId, item_id: itemId } = source.config;
  if (source.type === 'sharepoint_site') {
    return places.some((place) => place.siteId === siteId);
  }
  if (source.type === 'sharepoint_library') {
    return places.some(
      (place) => place.level !== 'site' && place.driveId === driveId,
    );
  }
  if (source.type === 'sharepoint_folder' && itemId) {
    return places.some(
      (place) =>
        place.driveId === driveId && place.rootAncestorItemIds.includes(itemId),
    );
  }
  return false;
}

/** Where a place for one knowledge source would sit, in the terms `createSharePointPlace` takes. */
export interface SourcePlaceAddress {
  level: SharePointPlaceLevel;
  siteId: string;
  driveId?: string;
  rootItemId?: string;
}

/**
 * The place that would put a source back inside the list — the source's own address as a place.
 *
 * Every source kind is a level a place can be — a site, a library, a folder, a single file — so the
 * source itself is the answer and one press restores it. A source missing an id its level needs has
 * no address here, and the caller then lets the person choose.
 */
export function placeForSource(
  source: Pick<KnowledgeSource, 'type' | 'config'>,
): SourcePlaceAddress | null {
  const siteId = source.config.site_id;
  if (!siteId) return null;
  const driveId = source.config.drive_id;
  if (source.type === 'sharepoint_site') return { level: 'site', siteId };
  if (!driveId) return null;
  if (source.type === 'sharepoint_library') {
    return { level: 'library', siteId, driveId };
  }
  const itemId = source.config.item_id;
  if (!itemId) return null;
  if (source.type === 'sharepoint_folder') {
    return { level: 'folder', siteId, driveId, rootItemId: itemId };
  }
  if (source.type === 'sharepoint_file') {
    return { level: 'file', siteId, driveId, rootItemId: itemId };
  }
  return null;
}

/** A row's address in the terms the server's coverage check takes. */
export interface AncestorCheckItem {
  siteId: string;
  driveId: string;
  itemId: string;
}

/**
 * The address to ask the server about, when ids alone cannot settle a row — or `null` when they can.
 *
 * {@link sharePointSourceCoverage} compares a folder by exact id, since a source stores no
 * ancestors. That is only uncertain for a folder or a file that is not itself a place while a
 * folder place sits on the same drive: it may be nested somewhere under that place. Everything else
 * — a site, a library, a row with no folder place on its drive, an unrestricted connection — the ids
 * decide, and the server is never asked about it.
 */
export function ancestorCheckItem(
  source: Pick<KnowledgeSource, 'type' | 'config'>,
  policy: SharePointPlacePolicy,
): AncestorCheckItem | null {
  if (!policy.enforced) return null;
  if (
    source.type !== 'sharepoint_folder' &&
    source.type !== 'sharepoint_file'
  ) {
    return null;
  }
  const { site_id: siteId, drive_id: driveId, item_id: itemId } = source.config;
  if (!siteId || !driveId || !itemId) return null;
  if (isAddressInsideAnyPlace(siteId, driveId, itemId, policy.places)) {
    return null;
  }
  const folderPlaceOnDrive = policy.places.some(
    (place) => place.level === 'folder' && place.driveId === driveId,
  );
  return folderPlaceOnDrive ? { siteId, driveId, itemId } : null;
}
